import React, { useId } from "react";
import {
  ResponsiveContainer,
  BarChart as ReBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { palette, tickStyle, tickStyleStrong, ChartTooltip, ChartEmpty } from "./ChartTheme";

/**
 * Minimal bar chart.
 * layout: "vertical" | "horizontal"
 * data: [{ name, value }]
 * Same props/behavior as before — visual pass only: soft gradient
 * fill, larger radius, dimmed non-hovered bars, shared tooltip.
 */
const BarChart = ({
  data = [],
  layout = "vertical",
  height = 200,
  color = palette.primary,
  categoryWidth = 100,
}) => {
  const gradientId = `barFill-${useId()}`;

  if (!data?.length) {
    return <ChartEmpty height={height} />;
  }

  const isHorizontal = layout === "horizontal";

  return (
    <div style={{ width: "100%", height }}>
      <ResponsiveContainer>
        <ReBarChart
          data={data}
          layout={isHorizontal ? "vertical" : "horizontal"}
          margin={{
            top: 4,
            right: 8,
            left: isHorizontal ? 4 : 0,
            bottom: 0,
          }}
          barCategoryGap={isHorizontal ? "28%" : "32%"}
        >
          <defs>
            <linearGradient
              id={gradientId}
              x1={isHorizontal ? "0" : "0"}
              y1={isHorizontal ? "0" : "0"}
              x2={isHorizontal ? "1" : "0"}
              y2={isHorizontal ? "0" : "1"}
            >
              <stop offset="0%" stopColor={color} stopOpacity={1} />
              <stop offset="100%" stopColor={color} stopOpacity={0.72} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={palette.grid}
            vertical={!isHorizontal}
            horizontal={isHorizontal}
          />
          {isHorizontal ? (
            <>
              <XAxis
                type="number"
                allowDecimals={false}
                tick={tickStyle}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                type="category"
                dataKey="name"
                width={categoryWidth}
                tick={tickStyleStrong}
                axisLine={false}
                tickLine={false}
              />
            </>
          ) : (
            <>
              <XAxis
                dataKey="name"
                tick={tickStyleStrong}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                tick={tickStyle}
                axisLine={false}
                tickLine={false}
                width={32}
              />
            </>
          )}
          <Tooltip
            cursor={{ fill: "#F8FAFC" }}
            content={<ChartTooltip />}
          />
          <Bar
            dataKey="value"
            fill={`url(#${gradientId})`}
            radius={isHorizontal ? [0, 6, 6, 0] : [6, 6, 0, 0]}
            maxBarSize={28}
            animationDuration={350}
          />
        </ReBarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default BarChart;